import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import logoBlanco from '../assets/marca/kodica-white.svg';
import { Iconos } from '../componentes/iconos';
import { Boton, Campo, Entrada, ErrorCaja } from '../componentes/ui';
import { ErrorApi } from '../lib/api';
import { esAppNativa, servidorEfectivo } from '../lib/servidor';
import { useSesion } from '../lib/sesion';
import { versionLarga } from '../lib/version';

export function Login() {
  const { usuario, iniciar } = useSesion();
  const [identificador, setIdentificador] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (usuario) return <Navigate to="/" replace />;

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await iniciar({ identificador, password });
    } catch (e) {
      // **Nombrar el servidor de verdad.** Este texto decía «de la caseta»
      // desde cuando la caseta era el único servidor que existía; hoy la app
      // habla con la nube y decirle a alguien que falla la caseta lo manda a
      // revisar el sitio equivocado.
      setError(
        e instanceof ErrorApi
          ? e.message
          : `No se pudo conectar con ${servidorEfectivo() ?? 'el servidor'}`,
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-stack-lg bg-on-primary-container p-gutter">
      {/* Logotipo en blanco sobre el tono oscuro de la rampa primaria: es la
          combinación que docs/10 exige que funcione siempre. */}
      <img src={logoBlanco} alt="Kodica" className="h-11 w-auto" />

      <div className="w-full max-w-sm rounded-xl border border-outline-variant bg-surface-container-lowest p-8">
        <header className="mb-stack-lg">
          <h1 className="text-headline-md text-on-surface">Control de acceso</h1>
          <p className="mt-1 text-body-md text-on-surface-variant">
            Entra con tu correo o tu teléfono
          </p>
        </header>

        <form onSubmit={enviar} className="space-y-5">
          <Campo etiqueta="Correo o teléfono">
            <Entrada
              id="identificador"
              // Sin autocapitalize ni autocorrect: en tablet, "Ana@" se
              // convierte solo en "Ana @" y el guardia no entiende por qué falla.
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              required
              value={identificador}
              onChange={(e) => setIdentificador(e.target.value)}
            />
          </Campo>

          <Campo etiqueta="Contraseña">
            <Entrada
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Campo>

          {error && <ErrorCaja>{error}</ErrorCaja>}

          <Boton
            type="submit"
            variante="primario"
            disabled={enviando}
            icono={Iconos.entrar}
            className="w-full"
          >
            {enviando ? 'Entrando…' : 'Entrar'}
          </Boton>
        </form>
      </div>

      {/* Solo en el APK: en el navegador el API vive en este mismo origen y no
          hay nada que elegir. Va discreto porque se toca una vez y ya. */}
      {esAppNativa() && (
        <Link
          to="/servidor"
          className="text-label-sm text-primary-fixed underline-offset-4 hover:underline"
        >
          Servidor: {servidorEfectivo() ?? 'sin elegir'}
        </Link>
      )}

      {/* Debajo del formulario y antes de la versión: quien llega aquí y no
          puede entrar necesita encontrarlo sin buscar. */}
      <Link
        to="/recuperar"
        className="text-label-sm text-primary underline-offset-4 hover:underline"
      >
        ¿Olvidaste tu contraseña?
      </Link>

      {/* La versión, antes de entrar. Aquí y no dentro de un menú porque la
          pregunta «¿cuál tienes instalada?» se hace por teléfono, a alguien que
          no sabe navegar la app y muchas veces ni ha podido entrar. */}
      <p className="text-label-sm text-primary-fixed opacity-70">{versionLarga()}</p>
    </div>
  );
}
